/**
 * HrV1AppraisalsIndex.tsx
 *
 * Cross-cycle list of Appraisal Forms. Lets HR browse and inspect any
 * appraisal without first picking a cycle.
 *
 * Route: /hr-v1/appraisals (HR-role only)
 */

import React, { useCallback, useEffect, useState } from 'react';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import AppraisalSummaryView from '../components/epr/AppraisalSummaryView';
import AppraisalClassificationCard from '../components/epr/AppraisalClassificationCard';
import { classifyAppraisal } from '../services/appraisalClassifier';
import { useToast } from '../components/ui/Toast';
import {
  collection, getDocs, query, where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { AppraisalForm, AppraisalStatus } from '../types/appraisal';
import type { ReviewCycle } from '../types';

interface RowWithCycle {
  appraisal: AppraisalForm;
  cycleName?: string;
}

const STATUS_TONE: Record<AppraisalStatus, 'info' | 'success' | 'warning' | 'neutral'> = {
  draft:             'neutral',
  sent_to_employee:  'info',
  employee_signed:   'info',
  manager_signed:    'success',
  closed:            'success',
};

const HrV1AppraisalsIndex: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<RowWithCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<AppraisalStatus | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cyclesSnap = await getDocs(
        query(collection(db, 'reviewCycles'), where('frameworkVersion', '==', 'hr_v1')),
      );
      const cycleNames: Record<string, string> = {};
      cyclesSnap.docs.forEach(d => {
        const c = d.data() as ReviewCycle;
        cycleNames[d.id] = c.name;
      });
      const cycleIds = cyclesSnap.docs.map(d => d.id);
      if (cycleIds.length === 0) {
        setRows([]);
        return;
      }
      // Firestore `in` clause max 10 (safer cross-version). Per-chunk try
      // so one denied chunk doesn't take down the whole list.
      const out: RowWithCycle[] = [];
      const chunks = chunk(cycleIds, 10);
      for (const ch of chunks) {
        try {
          const appsSnap = await getDocs(
            query(collection(db, 'appraisalForms'), where('cycleId', 'in', ch)),
          );
          for (const d of appsSnap.docs) {
            const a = { id: d.id, ...(d.data() as Omit<AppraisalForm, 'id'>) };
            out.push({ appraisal: a, cycleName: cycleNames[a.cycleId] });
          }
        } catch (chunkErr) {
          console.warn('[appraisals] chunk load failed, skipping:', chunkErr);
        }
      }
      // Sort: most recent first (updatedAt/createdAt fallback)
      out.sort((x, y) => {
        const tx = (x.appraisal.updatedAt ?? x.appraisal.createdAt)?.toMillis?.() ?? 0;
        const ty = (y.appraisal.updatedAt ?? y.appraisal.createdAt)?.toMillis?.() ?? 0;
        return ty - tx;
      });
      setRows(out);
    } catch (e) {
      toast.show(`Failed to load: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const filtered = rows.filter(r => filterStatus === 'all' || r.appraisal.status === filterStatus);
  const openRow  = openId ? rows.find(r => r.appraisal.id === openId) : null;

  if (loading) return <CenterMessage tone="info" title="Loading appraisals..." />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Console</div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Appraisal Forms</h1>
          <p className="text-sm text-stone-600 mt-1">
            {rows.length} appraisal form{rows.length !== 1 ? 's' : ''} across all hr_v1 cycles
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-stone-600">Filter status:</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as AppraisalStatus | 'all')}
            className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="sent_to_employee">Sent to employee</option>
            <option value="employee_signed">Employee signed</option>
            <option value="manager_signed">Manager signed</option>
            <option value="closed">Closed</option>
          </select>
          <Button variant="secondary" icon="refresh" onClick={() => void load()}>Refresh</Button>
        </div>
      </header>

      {filtered.length === 0 ? (
        <CenterMessage
          tone="info"
          title="No appraisals yet"
          body="Appraisal Forms appear after HR runs Step 3 (Consolidation) in the hr_v1 console."
        />
      ) : (
        <div className="space-y-2">
          {filtered.map(row => (
            <AppraisalRow
              key={row.appraisal.id}
              row={row}
              onOpen={() => setOpenId(row.appraisal.id)}
            />
          ))}
        </div>
      )}

      {openRow && (
        <AppraisalDetailModal
          row={openRow}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
};

const AppraisalRow: React.FC<{
  row: RowWithCycle;
  onOpen: () => void;
}> = ({ row, onOpen }) => {
  const a = row.appraisal;
  const hasEmpSig = Boolean(a.employeeSignature);
  const hasMgrSig = Boolean(a.managerSignature);
  const hasDisagree = Boolean(a.employeeDisagreeComment);
  const eprScore = a.eprSummary?.componentScores?.finalTotal;
  const score360 = a.reviewSummary360?.overallAverage;
  const classification = classifyAppraisal(eprScore, score360);

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-4 flex flex-wrap items-center gap-3 justify-between"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold text-stone-900">{a.employeeId}</div>
          <DisclosureBadge label={a.status} tone={STATUS_TONE[a.status]} />
          {classification.bucket !== 'incomplete' && (
            <AppraisalClassificationCard
              classification={classification}
              eprScore={eprScore}
              score360={score360}
              compact
            />
          )}
          {hasDisagree && <DisclosureBadge icon="report_problem" label="disagree" tone="warning" />}
          {hasEmpSig && <DisclosureBadge icon="draw" label="employee ✓" tone="success" />}
          {hasMgrSig && <DisclosureBadge icon="draw" label="manager ✓" tone="success" />}
        </div>
        <div className="text-xs text-stone-500 mt-1">
          Cycle: <code>{row.cycleName ?? a.cycleId}</code>
          {' · '}
          EPR total: <span className="font-medium text-stone-700">
            {fmt(a.eprSummary?.componentScores?.finalTotal)}
          </span>
          {' / 5.00'}
          {' · '}
          360 avg: <span className="font-medium text-stone-700">
            {fmt(a.reviewSummary360?.overallAverage)}
          </span>
          {' / 5.00'}
        </div>
      </div>
      <Button variant="primary" icon="visibility" onClick={onOpen}>View</Button>
    </section>
  );
};

const AppraisalDetailModal: React.FC<{
  row: RowWithCycle;
  onClose: () => void;
}> = ({ row, onClose }) => (
  <div
    className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4"
    role="dialog" aria-modal="true"
  >
    <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Appraisal — {row.appraisal.employeeId}</h3>
          <div className="text-xs text-stone-500">
            Cycle: {row.cycleName ?? row.appraisal.cycleId} · Status: {row.appraisal.status}
          </div>
        </div>
        <Button variant="tertiary" icon="close" onClick={onClose}>Tutup</Button>
      </div>
      <AppraisalSummaryView appraisal={row.appraisal} />
    </div>
  </div>
);

function fmt(n: number | undefined | null): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default HrV1AppraisalsIndex;
